import React, { useEffect, useRef, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    MenuItem,
    TextField,
    LinearProgress,
    Tooltip,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import PhoneInTalkRoundedIcon from '@material-ui/icons/PhoneInTalkRounded';
import CheckCircleRoundedIcon from '@material-ui/icons/CheckCircleRounded';
import HttpService from '../services/httpService';
import { setSnackInfo } from '../redux/actions/appAction';
import { hasRouteAccess, isRouteAccessReady } from '../utils/routeAccess';
import { maskPhoneNumbersInLogText } from '../utils/index';

export const CALLING_DESK_PATH = '/calling-desk';
export const CALLING_DESK_RESULTS_PATH = '/calling-desk/results';

// Mirrors MAX_LEADS_PER_REQUEST in quotation_tool_server routes/callingDesk.js
// (the server enforces it) - lets a summary cell refuse before fetching its leads.
export const CALLING_DESK_MAX_LEADS = 500;

const MAX_SCHEDULE_DAYS = 14;
// After the first call the rest go out with the scheduled calling runs, which run
// 4 times a day (quotation_tool_server routes/callingDesk.js enforces it too).
const MAX_CALLS_PER_DAY = 4;
const POLL_INTERVAL_MS = 2000;

const todayIst = () => new Date(Date.now() + 330 * 60 * 1000).toISOString().slice(0, 10);

const addDays = (iso, days) => {
    const [year, month, date] = iso.split('-').map(Number);
    const shifted = new Date(Date.UTC(year, month - 1, date + days));
    const mm = String(shifted.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(shifted.getUTCDate()).padStart(2, '0');
    return `${shifted.getUTCFullYear()}-${mm}-${dd}`;
};
const MAX_POLL_MS = 10 * 60 * 1000;

// The sidebar tab and the Project Logs button follow the Route Access grant for
// /calling-desk. If that list never loads (this local inventory server has no
// /route-access API), keep the feature visible - same as the rest of the app
// when the access list is unavailable.
export const useCanOpenRoute = (path) => {
    const { routeAccess } = useSelector((state) => state.user);
    if (routeAccess?.status === 'error') return true;
    return isRouteAccessReady(routeAccess) && hasRouteAccess(path, true, routeAccess);
};

export const useCanUseCallingDesk = () => useCanOpenRoute(CALLING_DESK_PATH);

const useStyles = makeStyles(() => ({
    callButton: {
        padding: 6,
        color: '#fff',
        background: 'linear-gradient(135deg, #2e7d32 0%, #43a047 100%)',
        boxShadow: '0 2px 6px rgba(46, 125, 50, 0.35)',
        '&:hover': {
            background: 'linear-gradient(135deg, #1b5e20 0%, #2e7d32 100%)',
        },
        '&.Mui-disabled': {
            color: 'rgba(0, 0, 0, 0.26)',
            background: '#eceff1',
            boxShadow: 'none',
        },
    },
    title: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        color: '#1C375D',
        fontWeight: 600,
    },
    titleIcon: {
        color: '#2e7d32',
    },
    subtitle: {
        color: '#666',
        fontSize: 13,
        marginTop: 2,
    },
    question: {
        fontSize: 16,
        color: '#1C375D',
        margin: '8px 0 4px',
    },
    typeGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        gap: 10,
        marginTop: 4,
    },
    typeOption: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        width: '100%',
        padding: '12px 14px',
        border: '1px solid #d9dee5',
        borderRadius: 8,
        background: '#fff',
        color: '#1C375D',
        fontSize: 14,
        textAlign: 'left',
        cursor: 'pointer',
        '&:hover': {
            borderColor: '#1C375D',
        },
        '&:disabled': {
            cursor: 'not-allowed',
            opacity: 0.6,
        },
    },
    typeOptionSelected: {
        borderColor: '#2e7d32',
        background: 'rgba(46, 125, 50, 0.08)',
        boxShadow: 'inset 0 0 0 1px #2e7d32',
    },
    // A call type the lead's stage doesn't allow - shown, greyed, with its condition.
    typeOptionBlocked: {
        background: '#f4f5f7',
        borderColor: '#e3e6ea',
        color: '#8a94a0',
        '&:hover': {
            borderColor: '#e3e6ea',
        },
        '&:disabled': {
            opacity: 1,
        },
    },
    typeIcon: {
        color: '#2e7d32',
        fontSize: 20,
    },
    typeIconBlocked: {
        color: '#b0b7bf',
        fontSize: 20,
    },
    typeText: {
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
    },
    typeCondition: {
        fontSize: 11,
        lineHeight: 1.3,
        color: '#8a94a0',
    },
    stageNote: {
        fontSize: 13,
        color: '#1C375D',
        margin: '0 0 8px',
    },
    note: {
        color: '#666',
        fontSize: 12,
        marginTop: 14,
    },
    scheduleFields: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        gap: 12,
        marginTop: 8,
    },
    progressText: {
        color: '#1C375D',
        margin: '12px 0 8px',
    },
    tiles: {
        display: 'grid',
        gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
        gap: 8,
    },
    tile: {
        border: '1px solid #e3e7ec',
        borderRadius: 8,
        padding: '10px 8px',
        textAlign: 'center',
    },
    tileValue: {
        fontSize: 22,
        fontWeight: 600,
    },
    tileLabel: {
        fontSize: 12,
        color: '#666',
    },
    issues: {
        marginTop: 14,
        maxHeight: 220,
        overflowY: 'auto',
        border: '1px solid #e3e7ec',
        borderRadius: 8,
    },
    issueRow: {
        padding: '8px 12px',
        fontSize: 13,
        borderBottom: '1px solid #f0f2f5',
        '&:last-child': {
            borderBottom: 'none',
        },
    },
}));

export const CallingDeskCallButton = ({ onClick, disabled, title = 'Call' }) => {
    const classes = useStyles();
    return (
        <Tooltip title={disabled ? 'No clients to call' : title}>
            <span>
                <IconButton size="small" className={classes.callButton} onClick={onClick} disabled={disabled} aria-label={title}>
                    <PhoneInTalkRoundedIcon style={{ fontSize: 18 }} />
                </IconButton>
            </span>
        </Tooltip>
    );
};

const clientLabel = (count) => `${count} ${count === 1 ? 'client' : 'clients'}`;

const SUMMARY_TILES = [
    { key: 'triggered', label: 'Calls placed', color: '#2e7d32' },
    { key: 'scheduled', label: 'Scheduled', color: '#1C375D', onlyIfAny: true },
    { key: 'skipped', label: 'Skipped', color: '#F47E46' },
    { key: 'failed', label: 'Failed', color: '#c62828' },
];

// Render this only while it is open ({target && <CallingDeskDialog ... />}) so
// every open starts from a clean slate. A summary cell passes `confirm` and is
// asked "schedule call for N clients?" first; a single lead (Project Logs, a
// row in the drill-down table) goes straight to the call type.
const CallingDeskDialog = ({ onClose, leadIds = [], confirm = false, source = 'calling-desk', subtitle, setSnackInfo }) => {
    const classes = useStyles();
    const count = leadIds.length;
    const [step, setStep] = useState(confirm || count > 1 ? 'confirm' : 'select');
    const [callTypes, setCallTypes] = useState([]);
    const [leadStages, setLeadStages] = useState([]);
    const [maxLeads, setMaxLeads] = useState(null);
    // Server clock decides - fetched on every open, so it is never stale.
    const [insideCallWindow, setInsideCallWindow] = useState(true);
    const [callTypesLoading, setCallTypesLoading] = useState(true);
    const [selectedType, setSelectedType] = useState('');
    const [startDate, setStartDate] = useState(todayIst);
    const [numberOfDays, setNumberOfDays] = useState('1');
    const [callsPerDay, setCallsPerDay] = useState('1');
    const [submitting, setSubmitting] = useState(false);
    const [batch, setBatch] = useState(null);
    const [pollTimedOut, setPollTimedOut] = useState(false);
    const pollTimer = useRef(null);
    // Set once the dialog is closed/unmounted - every async step checks it
    // after its await, so a late response never restarts polling.
    const closedRef = useRef(false);

    useEffect(() => {
        loadCallTypes();
        return () => {
            closedRef.current = true;
            clearTimeout(pollTimer.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadCallTypes = async () => {
        try {
            const response = await HttpService.getCallingDeskCallTypes(leadIds);
            if (closedRef.current) return;
            setCallTypes(response?.data?.callTypes || []);
            setLeadStages(response?.data?.leadStages || []);
            setMaxLeads(response?.data?.maxLeadsPerRequest || null);
            setInsideCallWindow(response?.data?.insideCallWindow !== false);
        } catch (error) {
            if (!closedRef.current) setSnackInfo(error?.response?.data?.message || 'Could not load call types', 'error');
        } finally {
            if (!closedRef.current) setCallTypesLoading(false);
        }
    };

    const pollBatch = (batchId, startedAt) => {
        pollTimer.current = setTimeout(async () => {
            try {
                const response = await HttpService.getCallingDeskBatch(batchId);
                if (closedRef.current) return;
                const latest = response?.data?.batch;
                if (latest) setBatch(latest);
                if (latest && latest.status !== 'processing') {
                    setStep('done');
                    return;
                }
            } catch (error) {
                // A dropped poll is retried on the next tick - the batch itself keeps running.
                if (closedRef.current) return;
            }
            if (Date.now() - startedAt < MAX_POLL_MS) {
                pollBatch(batchId, startedAt);
            } else {
                setPollTimedOut(true);
                setStep('done');
            }
        }, POLL_INTERVAL_MS);
    };

    const handleInitiate = async () => {
        if (!selectedType || submitting) return;
        setSubmitting(true);
        try {
            const response = await HttpService.initiateCallingDeskCalls({
                leadIds,
                callType: selectedType,
                source,
                startDate,
                numberOfDays: Number(numberOfDays),
                callsPerDay: Number(callsPerDay),
            });
            if (closedRef.current) return;
            if (!response?.data?.success) {
                setSnackInfo(response?.data?.message || 'Could not initiate the call', 'error');
                return;
            }
            setBatch({ status: 'processing', requested: response.data.requested, processed: 0 });
            setStep('progress');
            pollBatch(response.data.batchId, Date.now());
        } catch (error) {
            if (!closedRef.current) setSnackInfo(error?.response?.data?.message || 'Could not initiate the call', 'error');
        } finally {
            if (!closedRef.current) setSubmitting(false);
        }
    };

    const handleClose = () => {
        // While the request is in flight the user cannot tell whether calls were
        // placed, so Esc / backdrop clicks wait for the answer.
        if (submitting) return;
        closedRef.current = true;
        clearTimeout(pollTimer.current);
        if (step === 'progress') {
            setSnackInfo('Calls are still being placed in the background', 'info');
        }
        onClose();
    };

    const overCap = !!maxLeads && count > maxLeads;
    const days = Number(numberOfDays);
    const perDay = Number(callsPerDay);
    const scheduleValid = Number.isInteger(days) && days >= 1 && days <= MAX_SCHEDULE_DAYS
        && Number.isInteger(perDay) && perDay >= 1 && perDay <= MAX_CALLS_PER_DAY
        && /^\d{4}-\d{2}-\d{2}$/.test(startDate)
        && startDate >= todayIst();
    const totalAttempts = scheduleValid ? days * perDay : 0;
    const startsToday = startDate === todayIst();

    const selectedLabel = callTypes.find((type) => type.key === selectedType)?.label;
    const issues = (batch?.details || []).filter((row) => row.status === 'skipped' || row.status === 'failed');
    const progressPercent = batch?.requested ? Math.round(((batch.processed || 0) / batch.requested) * 100) : 0;

    return (
        <Dialog open onClose={handleClose} fullWidth maxWidth="sm">
            <DialogTitle disableTypography>
                <div className={classes.title}>
                    <PhoneInTalkRoundedIcon className={classes.titleIcon} />
                    <span>Calling Desk</span>
                </div>
                {subtitle && <div className={classes.subtitle}>{maskPhoneNumbersInLogText(subtitle)}</div>}
            </DialogTitle>

            <DialogContent dividers>
                {step === 'confirm' && (
                    <>
                        <div className={classes.question}>Do you want to schedule call for {clientLabel(count)}?</div>
                        {overCap && (
                            <div className={classes.note} style={{ color: '#c62828' }}>
                                At most {maxLeads} clients can be called at once. Narrow the filters (date range, experience centre, users) and try again.
                            </div>
                        )}
                    </>
                )}

                {step === 'select' && (
                    <>
                        <div className={classes.question}>
                            Which call do you want to place for {clientLabel(count)}?
                        </div>
                        {callTypesLoading ? (
                            <div style={{ textAlign: 'center', padding: 24 }}><CircularProgress size={28} /></div>
                        ) : (
                            <>
                            {leadStages.length > 0 && (
                                <div className={classes.stageNote}>
                                    Lead stage: {leadStages.map((stage) => (count > 1 ? `${stage.label} (${stage.count})` : stage.label)).join(', ')}
                                </div>
                            )}
                            <div className={classes.typeGrid}>
                                {callTypes.map((type) => {
                                    // Every call type is listed; the ones no selected lead's
                                    // stage allows are greyed out with the stage they need.
                                    const allowed = type.allowedLeads ?? count;
                                    const blocked = allowed === 0;
                                    return (
                                        <button
                                            key={type.key}
                                            type="button"
                                            className={`${classes.typeOption} ${blocked ? classes.typeOptionBlocked : ''} ${selectedType === type.key ? classes.typeOptionSelected : ''}`}
                                            onClick={() => setSelectedType(type.key)}
                                            disabled={submitting || blocked}
                                        >
                                            <PhoneInTalkRoundedIcon className={blocked ? classes.typeIconBlocked : classes.typeIcon} />
                                            <span className={classes.typeText}>
                                                <span>{type.label}</span>
                                                {blocked && <span className={classes.typeCondition}>{type.condition}</span>}
                                                {!blocked && allowed < count && (
                                                    <span className={classes.typeCondition}>For {allowed} of {clientLabel(count)} - the rest are skipped</span>
                                                )}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                            </>
                        )}
                        <div className={classes.note}>
                            {insideCallWindow
                                ? 'AI calls can be placed between 9 AM and 9 PM IST.'
                                : <span style={{ color: '#c62828' }}>AI calls can only be placed between 9 AM and 9 PM IST - please try again during calling hours.</span>}
                        </div>
                    </>
                )}

                {step === 'schedule' && (
                    <>
                        <div className={classes.question}>
                            When should the {selectedLabel || 'selected'} call go out for {clientLabel(count)}?
                        </div>
                        <div className={classes.scheduleFields}>
                            <TextField
                                label="Start date"
                                type="date"
                                value={startDate}
                                onChange={(event) => setStartDate(event.target.value)}
                                inputProps={{ min: todayIst(), max: addDays(todayIst(), 30) }}
                                InputLabelProps={{ shrink: true }}
                                variant="outlined"
                                size="small"
                                fullWidth
                            />
                            <TextField
                                label="Number of days"
                                type="number"
                                value={numberOfDays}
                                onChange={(event) => setNumberOfDays(event.target.value)}
                                inputProps={{ min: 1, max: MAX_SCHEDULE_DAYS }}
                                variant="outlined"
                                size="small"
                                fullWidth
                            />
                            <TextField
                                select
                                label="Calls per day"
                                value={callsPerDay}
                                onChange={(event) => setCallsPerDay(event.target.value)}
                                variant="outlined"
                                size="small"
                                fullWidth
                            >
                                {Array.from({ length: MAX_CALLS_PER_DAY }, (unused, index) => String(index + 1)).map((value) => (
                                    <MenuItem key={value} value={value}>{value}</MenuItem>
                                ))}
                            </TextField>
                        </div>
                        <div className={classes.note}>
                            {!scheduleValid
                                ? `Choose a start date, 1 to ${MAX_SCHEDULE_DAYS} days, and 1 to ${MAX_CALLS_PER_DAY} calls per day.`
                                : totalAttempts === 1
                                    ? `${clientLabel(count)}. One call, ${startsToday ? 'placed now' : `on ${startDate}`}.`
                                    : `${clientLabel(count)}. ${startsToday ? 'The first call goes out now; the rest go out' : `Calls start on ${startDate} and go out`} with the scheduled calling runs - up to ${perDay} a day for ${days} day${days === 1 ? '' : 's'}, 9 AM to 9 PM IST - and stop once the customer answers.`}
                        </div>
                    </>
                )}

                {step === 'progress' && (
                    <>
                        <div className={classes.progressText}>
                            Placing {selectedLabel ? `"${selectedLabel}"` : ''} calls - {batch?.processed || 0} of {batch?.requested || count} done
                        </div>
                        <LinearProgress variant="determinate" value={progressPercent} />
                    </>
                )}

                {step === 'done' && (
                    <>
                        {batch?.status === 'completed' && (
                            <div className={classes.progressText} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <CheckCircleRoundedIcon style={{ color: '#2e7d32' }} />
                                {selectedLabel || batch?.callTypeLabel} - {clientLabel(batch?.requested || count)} processed
                            </div>
                        )}
                        {batch?.status === 'failed' && (
                            <div className={classes.progressText} style={{ color: '#c62828' }}>
                                The run stopped part-way: {batch?.errorMessage || 'unexpected error'}
                            </div>
                        )}
                        {pollTimedOut && batch?.status === 'processing' && (
                            <div className={classes.progressText}>
                                Still running - calls continue in the background. Check the lead's activity for updates.
                            </div>
                        )}
                        <div className={classes.tiles}>
                            {SUMMARY_TILES.filter((tile) => !tile.onlyIfAny || batch?.[tile.key]).map((tile) => (
                                <div key={tile.key} className={classes.tile}>
                                    <div className={classes.tileValue} style={{ color: tile.color }}>{batch?.[tile.key] || 0}</div>
                                    <div className={classes.tileLabel}>{tile.label}</div>
                                </div>
                            ))}
                        </div>
                        {issues.length > 0 && (
                            <div className={classes.issues}>
                                {issues.map((row) => (
                                    <div key={row.leadId} className={classes.issueRow}>
                                        <b>{row.leadNo || 'Lead'}</b>{row.customerName ? ` (${row.customerName})` : ''} - {row.reason}
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                )}
            </DialogContent>

            <DialogActions>
                {step === 'confirm' && (
                    <>
                        <Button onClick={handleClose} color="default">No</Button>
                        <Button onClick={() => setStep('select')} variant="contained" color="primary" disabled={overCap}>Yes, continue</Button>
                    </>
                )}
                {step === 'select' && (
                    <>
                        <Button onClick={handleClose} color="default" disabled={submitting}>Cancel</Button>
                        <Button
                            onClick={() => setStep('schedule')}
                            variant="contained"
                            color="primary"
                            disabled={!selectedType}
                        >
                            Next
                        </Button>
                    </>
                )}
                {step === 'schedule' && (
                    <>
                        <Button onClick={() => setStep('select')} color="default" disabled={submitting}>Back</Button>
                        <Button
                            onClick={handleInitiate}
                            variant="contained"
                            style={scheduleValid && !submitting ? { backgroundColor: '#2e7d32', color: '#fff' } : undefined}
                            disabled={!scheduleValid || submitting}
                            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <PhoneInTalkRoundedIcon />}
                        >
                            {count === 1 ? 'Initiate call for 1 client' : `Initiate calls for ${count} clients`}
                        </Button>
                    </>
                )}
                {step === 'progress' && <Button onClick={handleClose} color="default">Close</Button>}
                {step === 'done' && <Button onClick={handleClose} variant="contained" color="primary">Done</Button>}
            </DialogActions>
        </Dialog>
    );
};

export default connect(null, { setSnackInfo })(CallingDeskDialog);
